import { useHistory } from 'react-router-dom';
import { useUser } from '../../user-provider';
import { Button, ButtonGroup } from 'react-bootstrap';
import CalendarDropdown from './calendar-dropdown';
import { generateSessionUrl } from '../../utilities/generate-session-url';
import { format, isPast, addMinutes } from 'date-fns';
import { V_LOGO, MEDIAURL } from '../../utilities/constants';
import './matchup.css';

const convertTag = (tag) => {
  switch (tag) {
    case 'MALE':
      return 'Mens';
    case 'FEMALE':
      return 'Womens';
    case 'STREAMING':
      return 'Streaming';
    default:
      return tag;
  }
};

const logoPath = (teamLogo) => {
  if (!!teamLogo) {
    return MEDIAURL + teamLogo;
  }

  return V_LOGO;
};

const showStreamLinks = (multiStream, handler, baseURL) => {
  const indices = multiStream && Object.keys(multiStream);

  if (!indices.length) {
    return null;
  }

  return (
    <div className="streamLinks">
      <h4 className="streamLinkTitle">Individual Streams</h4>
      <ButtonGroup>
        {indices.map((i) => (
          <Button
            as="a"
            href={`${baseURL}&stream=${i}`}
            variant="light"
            onClick={(e) => handler(e, i)}
            key={i}
          >{`${multiStream?.[i]}`}</Button>
        ))}
      </ButtonGroup>
    </div>
  );
};

function MatchUp({ match, disabled }) {
  const {
    sessionId,
    teamA,
    teamALogo,
    teamB,
    teamBLogo,
    teamC,
    teamCLogo,
    teamD,
    teamDLogo,
    teamE,
    teamELogo,
    teamF,
    teamFLogo,
    teamG,
    teamGLogo,
    teamH,
    teamHLogo,
    gameTime,
    title,
    multiStream,
    useBeta,
    tags,
  } = match;
  const vsTitle = [
    teamA,
    teamB ? teamB : null,
    teamC ? teamC : null,
    teamD ? teamD : null,
    teamE ? teamE : null,
    teamF ? teamF : null,
    teamG ? teamG : null,
    teamH ? teamH : null,
  ]
    .filter(Boolean)
    .join(' vs ');
  const matchUpTitle = title ? title : `${vsTitle}`;
  const matchUpDate = `${format(new Date(gameTime), "EEE M'/'dd'/'yyyy @ p")}`;
  const history = useHistory();
  const user = useUser();
  const calendarEventTitle = `Virtius: ${vsTitle}`;
  const calendarEventDescription = generateSessionUrl(sessionId, true);
  const fanEarlyBlock =
    user.isFan && !isPast(addMinutes(new Date(gameTime), -1440)) && false;

  const isAuthenticated = !!user.user?.signInUserSession;
  const hasMultiStream = !!multiStream;
  const betaURL = useBeta ? '&beta=fan' : '';
  const scoresURL = `https://scores.virti.us/?s=${sessionId}`;
  const appURL = generateSessionUrl(`${sessionId}${betaURL}`);
  // If multiStream exists, always point main button to the app session link
  const primaryURL = hasMultiStream || isAuthenticated ? appURL : scoresURL;

  const handler = (e, streamKey) => {
    e.preventDefault();
    const streamURL = hasMultiStream && streamKey ? `&stream=${streamKey}` : '';

    // If multiStream, always go to the app session link
    if (hasMultiStream) {
      const sessionURL = generateSessionUrl(
        `${sessionId}${betaURL}${streamURL}`
      );
      history.push(sessionURL);
    } else if (e.shiftKey || !isAuthenticated) {
      // Hold Shift to go to public scores page (for authenticated users)
      window.location.href = `https://scores.virti.us/?s=${sessionId}${streamURL}`;
    } else {
      // Authenticated users go to the full app experience
      const sessionURL = generateSessionUrl(
        `${sessionId}${betaURL}${streamURL}`
      );
      history.push(sessionURL);
    }
  };

  return (
    <div className="heroMessage">
      <p className="matchTitle">{matchUpTitle}</p>
      <p className="matchDate">{matchUpDate}</p>
      {tags && tags.length > 0 && (
        <div className="tagPills">
          {tags.map((tag, index) => {
            const tagClass =
              tag === 'MALE'
                ? 'mens'
                : tag === 'FEMALE'
                ? 'womens'
                : tag === 'STREAMING'
                ? 'streaming'
                : 'default';

            if (tag === 'FEMALE' || tag === '') return null;

            return (
              <span key={index} className={`tagPill ${tagClass}`}>
                {convertTag(tag)}
              </span>
            );
          })}
        </div>
      )}
      <div className="matchLogos">
        <Button
          as="a"
          href={primaryURL}
          variant="outline-light"
          onClick={handler}
          className="matchButton"
          disabled={disabled || fanEarlyBlock}
        >
          <img src={logoPath(teamALogo)} alt={teamA} />
          {teamB && !teamD ? (
            <>
              <span className="vBlue">V</span>
              <span className="vGray">S</span>
            </>
          ) : null}
          {teamB && <img src={logoPath(teamBLogo)} alt={teamB} />}
          {teamC && !teamD ? (
            <>
              <span className="vBlue">V</span>
              <span className="vGray">S</span>
            </>
          ) : null}
          {teamC && <img src={logoPath(teamCLogo)} alt={teamC} />}
          {teamD && <img src={logoPath(teamDLogo)} alt={teamD} />}
          {teamE && <img src={logoPath(teamELogo)} alt={teamE} />}
          {teamF && <img src={logoPath(teamFLogo)} alt={teamF} />}
          {teamG && <img src={logoPath(teamGLogo)} alt={teamG} />}
          {teamH && <img src={logoPath(teamHLogo)} alt={teamH} />}
        </Button>
      </div>
      {!!multiStream ? showStreamLinks(multiStream, handler, appURL) : null}
      <div className="calendarAdd">
        <CalendarDropdown
          eventTitle={calendarEventTitle}
          eventDescription={calendarEventDescription}
          sessionId={sessionId}
          startAt={gameTime}
          handler={handler}
          disabled={disabled}
          isAuthenticated={isAuthenticated}
          hasMultiStream={hasMultiStream}
          scoresURL={scoresURL}
          appURL={generateSessionUrl(sessionId, true)}
        />
      </div>
    </div>
  );
}

export default MatchUp;
